import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { Dialog } from './Dialog.tsx';

/**
 * The single error surface for the whole platform.
 *
 * Project rule: a failure reaches the user in a modal — never inline text,
 * never `alert()`, never a toast that scrolls away before it is read. Toasts
 * carry successes; field-level validation stays inline beside its field; an
 * operation that FAILED opens this.
 */

export interface ErrorDetail {
  /** What the user was trying to do, in their words. */
  title: string;
  /** What happened, and what they can do next. */
  message: string;
  /** Machine code from the API envelope, shown small for support to quote. */
  code?: string;
  /** Correlates with the server log line. */
  requestId?: string;
}

interface ErrorDialogApi {
  showError: (detail: ErrorDetail) => void;
  /** Convenience for a caught value of unknown shape. */
  showUnexpected: (error: unknown, title?: string) => void;
}

const Ctx = createContext<ErrorDialogApi | null>(null);

export function useErrorDialog(): ErrorDialogApi {
  const ctx = useContext(Ctx);
  if (!ctx) {
    throw new Error(
      'useErrorDialog must be used inside <ErrorDialogProvider>. Mount it at the app root so every failure has somewhere to go.',
    );
  }
  return ctx;
}

export function ErrorDialogProvider({ children }: { children: ReactNode }) {
  const [detail, setDetail] = useState<ErrorDetail | null>(null);

  const showError = useCallback((d: ErrorDetail) => setDetail(d), []);

  const showUnexpected = useCallback((error: unknown, title = 'Something went wrong') => {
    // Never render a raw object: a stringified Error leaks internals into the UI
    // and a stringified object renders as "[object Object]".
    const message =
      error instanceof Error && error.message
        ? error.message
        : 'The action could not be completed. Try again, and contact support if it keeps happening.';
    const code =
      typeof error === 'object' && error !== null && 'code' in error
        ? String((error as { code: unknown }).code)
        : undefined;
    const requestId =
      typeof error === 'object' && error !== null && 'requestId' in error
        ? String((error as { requestId: unknown }).requestId)
        : undefined;
    setDetail({ title, message, ...(code ? { code } : {}), ...(requestId ? { requestId } : {}) });
  }, []);

  const api = useMemo<ErrorDialogApi>(() => ({ showError, showUnexpected }), [showError, showUnexpected]);

  return (
    <Ctx.Provider value={api}>
      {children}
      <Dialog
        open={detail !== null}
        onClose={() => setDetail(null)}
        title={detail?.title ?? ''}
        actions={
          <button type="button" className="btn btn-primary" onClick={() => setDetail(null)}>
            Close
          </button>
        }
      >
        <div className="stack">
          <p style={{ margin: 0 }}>{detail?.message}</p>
          {detail?.code || detail?.requestId ? (
            <p className="dialog-meta">
              {detail.code ? <span>Code {detail.code}</span> : null}
              {detail.code && detail.requestId ? ' · ' : null}
              {detail.requestId ? <span>Reference {detail.requestId}</span> : null}
            </p>
          ) : null}
        </div>
      </Dialog>
    </Ctx.Provider>
  );
}
